<template>
  <div class="panel">
    <a-spin :spinning="loading">
      <a-table
        :columns="columns"
        :data-source="featuredList"
        :pagination="false"
        row-key="position"
        size="middle"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'advertiser'">
            {{ record.advertiserName || record.name || '-' }}
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button
              type="link"
              class="edit-link"
              @click="openEditModal(record)"
            >
              Edit
            </a-button>
          </template>
        </template>
      </a-table>
    </a-spin>

    <a-modal
      v-model:open="editModalOpen"
      title="Edit"
      :confirm-loading="saving"
      ok-text="Save"
      destroy-on-close
      :mask-closable="false"
      @ok="onSave"
      @cancel="closeEditModal"
    >
      <div class="edit-field">
        <div class="edit-label">
          Current Advertiser:
        </div>
        <div class="edit-value">
          {{ currentRecord?.advertiserName || currentRecord?.name || '-' }}
        </div>
      </div>
      <div class="edit-field">
        <div class="edit-label">
          Select New Advertiser
        </div>
        <a-select
          v-model:value="selectedAdvertiserId"
          show-search
          allow-clear
          placeholder="Please enter"
          style="width: 100%"
          :filter-option="false"
          :not-found-content="advertiserState.fetching ? undefined : null"
          :options="advertiserState.data"
          :get-popup-container="(triggerNode) => triggerNode.parentNode"
          @search="onAdvertiserSearch"
        >
          <template
            v-if="advertiserState.fetching"
            #notFoundContent
          >
            <a-spin size="small" />
          </template>
        </a-select>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { debounce } from 'lodash-es'
import { message } from 'ant-design-vue'
import { settings } from '../../server/index'

const FEATURED_SLOT_COUNT = 6

const loading = ref(false)
const saving = ref(false)
const featuredList = ref([])
const editModalOpen = ref(false)
const currentRecord = ref(null)
const selectedAdvertiserId = ref(undefined)

const advertiserState = reactive({
  data: [],
  fetching: false,
})

const columns = [
  { title: 'Position', dataIndex: 'position', key: 'position', width: 220 },
  { title: 'Advertiser', dataIndex: 'advertiserName', key: 'advertiser' },
  { title: 'Action', key: 'action', width: 120 },
]

function normalizeFeaturedList(list) {
  const map = new Map()
  ;(list || []).forEach((item) => {
    const position = Number(item.position)
    if (!position) return
    map.set(position, {
      position,
      id: item.id,
      advertiserId: item.advertiserId ?? item.id ?? null,
      advertiserName: item.advertiserName || item.name || '',
    })
  })
  return Array.from({ length: FEATURED_SLOT_COUNT }, (_, index) => {
    const position = index + 1
    return (
      map.get(position) || {
        position,
        advertiserId: null,
        advertiserName: '',
      }
    )
  })
}

function loadFeaturedList() {
  loading.value = true
  settings
    .getFeaturedAdvertiserList()
    .then((res) => {
      if (res.success) {
        featuredList.value = normalizeFeaturedList(res.data)
      } else {
        message.error(res.error || res.message || 'Failed to load featured advertisers.')
        featuredList.value = normalizeFeaturedList([])
      }
    })
    .catch(() => {
      featuredList.value = normalizeFeaturedList([])
    })
    .finally(() => {
      loading.value = false
    })
}

function openEditModal(record) {
  currentRecord.value = { ...record }
  selectedAdvertiserId.value = record.advertiserId || undefined
  advertiserState.data = record.advertiserId
    ? [
        {
          label: record.advertiserName || record.name || String(record.advertiserId),
          value: record.advertiserId,
        },
      ]
    : []
  editModalOpen.value = true
}

function closeEditModal() {
  editModalOpen.value = false
  currentRecord.value = null
  selectedAdvertiserId.value = undefined
  advertiserState.data = []
  advertiserState.fetching = false
}

const onAdvertiserSearch = debounce((value) => {
  if (!value) {
    advertiserState.data = []
    return
  }
  advertiserState.fetching = true
  settings
    .getAdvertiserSuggestionList({ name: value, size: 999 })
    .then((res) => {
      if (res.success) {
        advertiserState.data = (res.data || []).map((item) => ({
          label: item.advertiserName || item.name,
          value: item.id,
        }))
      }
    })
    .finally(() => {
      advertiserState.fetching = false
    })
}, 300)

function onSave() {
  console.log(currentRecord.value)
  if (!currentRecord.value) {
    return Promise.reject()
  }
  if (!selectedAdvertiserId.value) {
    message.error('Please select a new advertiser.')
    return Promise.reject()
  }
  saving.value = true
  return settings
    .updateFeaturedAdvertiser({
      id: currentRecord.value.id,
      advertiserId: selectedAdvertiserId.value,
    })
    .then((res) => {
      if (res.success) {
        message.success('Saved successfully.')
        closeEditModal()
        loadFeaturedList()
      } else {
        message.error(res.error || res.message || 'Save failed.')
        return Promise.reject()
      }
    })
    .finally(() => {
      saving.value = false
    })
}

onMounted(() => {
  loadFeaturedList()
})
</script>

<style lang="less" scoped>
.panel {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px 20px;
}
.edit-link {
  padding: 0;
}
.edit-field {
  margin-bottom: 16px;
}
.edit-label {
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
}
.edit-value {
  color: rgba(0, 0, 0, 0.88);
  margin-bottom: 4px;
}
</style>
